import { useState } from "react";
import axios from "axios";
import { Bell, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** "Notify me" email capture for a product that is not publicly launched yet. */
export function WaitlistForm({ productKey, productName }) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setFeedback("");
    try {
      const res = await axios.post(`${API}/waitlist`, { email, product: productKey, website });
      setStatus("success");
      setFeedback(res.data.message);
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setFeedback(
        typeof detail === "string" ? detail : "We couldn't save your email. Please try again.",
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid={`waitlist-success-message-${productKey}`}
        className="flex items-start gap-3 rounded-lg border border-royal/30 bg-royal-light/60 px-4 py-3"
      >
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-royal text-white">
          <Check className="h-3 w-3" aria-hidden="true" />
        </span>
        <p className="text-sm text-steel leading-relaxed">{feedback}</p>
      </div>
    );
  }

  return (
    <form
      data-testid={`waitlist-form-${productKey}`}
      onSubmit={handleSubmit}
      className="space-y-2"
      aria-label={`Get notified when ${productName} launches`}
    >
      <label
        htmlFor={`waitlist-email-${productKey}`}
        className="flex items-center gap-1.5 text-[11px] font-mono-sub font-semibold uppercase tracking-wider text-mute"
      >
        <Bell className="h-3 w-3 text-royal" aria-hidden="true" />
        Get notified at launch
      </label>
      <div className="flex gap-2">
        <input
          id={`waitlist-email-${productKey}`}
          data-testid={`waitlist-email-input-${productKey}`}
          type="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="min-w-0 flex-1 rounded border border-line bg-white px-3 py-2.5 text-sm text-steel placeholder:text-slate-400 focus:border-royal focus:outline-none transition-colors"
        />
        <button
          type="submit"
          data-testid={`waitlist-submit-button-${productKey}`}
          disabled={status === "submitting"}
          className="shrink-0 rounded bg-steel px-4 py-2.5 text-xs font-mono-sub font-bold uppercase tracking-wider text-white hover:bg-steel-light disabled:opacity-60 transition-colors"
        >
          {status === "submitting" ? "Saving…" : "Notify me"}
        </button>
      </div>
      {/* Honeypot: invisible to humans, attractive to bots. Never fill this. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        data-testid={`waitlist-honeypot-input-${productKey}`}
      />
      {status === "error" && (
        <p data-testid={`waitlist-error-message-${productKey}`} className="text-xs text-red-600">
          {feedback}
        </p>
      )}
    </form>
  );
}
